import { EnergyConfigEntry } from '../types/energy';

/**
 * Calculate the total number of stars earned from total energy.
 * Star n costs: initialThreshold * growthRate^n (n=0,1,2,...)
 * A star is earned only when energy strictly EXCEEDS the threshold.
 */
export function getTotalStarsEarned(
  totalEnergy: number,
  initialThreshold: number,
  growthRate: number
): number {
  if (totalEnergy <= 0 || initialThreshold <= 0) return 0;

  let remaining = totalEnergy;
  let starCount = 0;

  while (true) {
    const threshold = initialThreshold * Math.pow(growthRate, starCount);
    if (remaining < threshold) break;
    remaining -= threshold;
    starCount++;
  }

  return starCount;
}

/**
 * Calculate the energy accumulated toward the next star.
 */
export function getCurrentEnergyInBar(
  totalEnergy: number,
  initialThreshold: number,
  growthRate: number
): number {
  if (totalEnergy <= 0 || initialThreshold <= 0) return 0;

  let remaining = totalEnergy;
  let starCount = 0;

  while (true) {
    const threshold = initialThreshold * Math.pow(growthRate, starCount);
    if (remaining < threshold) break;
    remaining -= threshold;
    starCount++;
  }

  return remaining;
}

/**
 * Calculate the threshold for the next star.
 */
export function getNextStarThreshold(
  totalEnergy: number,
  initialThreshold: number,
  growthRate: number
): number {
  const starsEarned = getTotalStarsEarned(totalEnergy, initialThreshold, growthRate);
  return initialThreshold * Math.pow(growthRate, starsEarned);
}

/**
 * Break down total stars into stars/moons/suns.
 * 3 stars = 1 moon, 3 moons = 1 sun
 */
export function getCelestialCounts(totalStars: number): { stars: number; moons: number; suns: number } {
  return {
    stars: totalStars % 3,
    moons: Math.floor(totalStars / 3) % 3,
    suns: Math.floor(totalStars / 9),
  };
}

/**
 * Get the config for a specific category.
 */
export function getConfigForCategory(
  config: EnergyConfigEntry[],
  category: string
): EnergyConfigEntry | undefined {
  return config.find(c => c.category === category);
}
